import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useStripeConnect } from '@/hooks/useStripeConnect';
import { CreditCard, CheckCircle, AlertCircle, RefreshCw, Shield, ExternalLink } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { usePaymentProcessingEnabled } from '@/hooks/usePaymentProcessingEnabled';

interface StripeConnectManagerProps {
  organizationId: string;
}

export function StripeConnectManager({ organizationId }: StripeConnectManagerProps) {
  const { user } = useAuth();
  const {
    stripeAccount,
    isLoading,
    isConnected,
    isPending,
    createConnectAccount,
    refreshAccountStatus,
    createLoginLink
  } = useStripeConnect(organizationId);

  const { paymentProcessingEnabled, isLoading: flagLoading } = usePaymentProcessingEnabled();

  if (flagLoading || !paymentProcessingEnabled) return null;

  const handleConnect = () => {
    const baseUrl = window.location.origin;
    const email = user?.email || '';
    
    createConnectAccount.mutate({
      email,
      refreshUrl: `${baseUrl}/settings/account`,
      returnUrl: `${baseUrl}/settings/account`,
    });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CreditCard className="h-5 w-5" />
          Stripe Connect
        </CardTitle>
        <CardDescription>
          Connect your Stripe account to receive payments from clients
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center p-8">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <>
            {isConnected ? (
              <div className="space-y-4">
                <Alert className="border-green-500/50 bg-green-500/10">
                  <CheckCircle className="h-4 w-4 text-green-500" />
                  <AlertDescription className="text-green-500">
                    Your Stripe account is connected and ready to receive payments
                  </AlertDescription>
                </Alert>

                <div className="flex items-center justify-between p-4 bg-secondary rounded-lg">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-muted-foreground">Status</p>
                      <Badge variant="outline" className="border-green-500 text-green-500">
                        Connected
                      </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        Charges Enabled
                      </span>
                      <span className="flex items-center gap-1">
                        <CheckCircle className="h-3 w-3 text-green-500" />
                        Payouts Enabled
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => createLoginLink.mutate()}
                      variant="default"
                      size="default"
                      disabled={createLoginLink.isPending}
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      {createLoginLink.isPending ? 'Opening...' : 'View Stripe Dashboard'}
                    </Button>
                    <Button
                      onClick={() => refreshAccountStatus.mutate()}
                      variant="outline"
                      size="icon"
                      disabled={refreshAccountStatus.isPending}
                    >
                      <RefreshCw className={`h-4 w-4 ${refreshAccountStatus.isPending ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </div>
              </div>
            ) : isPending ? (
              <div className="space-y-4">
                <Alert className="border-yellow-500/50 bg-yellow-500/10">
                  <AlertCircle className="h-4 w-4 text-yellow-500" />
                  <AlertDescription className="text-yellow-500">
                    Your Stripe account setup is incomplete. Please complete the onboarding process.
                  </AlertDescription>
                </Alert>

                <div className="flex items-center justify-between p-4 bg-secondary rounded-lg">
                  <div className="flex-1 space-y-2">
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-muted-foreground">Status</p>
                      <Badge variant="outline" className="border-yellow-500 text-yellow-500">
                        Pending
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Complete your onboarding to start receiving payments
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={handleConnect}
                      disabled={createConnectAccount.isPending}
                    >
                      Complete Setup
                    </Button>
                    <Button
                      onClick={() => refreshAccountStatus.mutate()}
                      variant="outline"
                      size="icon"
                      disabled={refreshAccountStatus.isPending}
                    >
                      <RefreshCw className={`h-4 w-4 ${refreshAccountStatus.isPending ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <Alert>
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>
                    Connect your Stripe account to receive payments from clients. You'll be redirected to Stripe to complete the setup.
                    <br /><br />
                    <strong>Secure Payment Processing:</strong> Your financial information is securely processed by Stripe. Radius never stores your banking details.
                  </AlertDescription>
                </Alert>

                <div className="flex items-center justify-between p-4 bg-secondary rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground">Status</p>
                    <p className="text-base font-medium text-foreground">Not Connected</p>
                  </div>
                  <Button
                    onClick={handleConnect}
                    disabled={createConnectAccount.isPending}
                  >
                    {createConnectAccount.isPending ? 'Connecting...' : 'Connect Stripe'}
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
